import { unit } from 'mathjs';
// import frLocale from 'date-fns/locale/fr';
// import enLocale from 'date-fns/locale/en-US';
import { unitMasks } from 'components/units/UnitDefinitionsAndMasks';
import {
  collection, getDocs, query, where,
} from 'firebase/firestore';

export function getWindowDimensions() {
  const { innerWidth: width, innerHeight: height } = window;
  return {
    width,
    height,
  };
}

export async function isUserAdmin(user) {
  if (user !== null) {
    const token = await user.getIdTokenResult();
    if (token?.claims.admin !== undefined) {
      return token.claims.admin === 'true';
    }
  }
  return false;
}

export async function isAlpha(user) {
  const token = await user.getIdTokenResult();
  if (token?.claims.alpha !== undefined) {
    return token.claims.alpha === 'true';
  }
  return false;
}

export function inAdminPage(location) {
  return location.pathname.includes('admin');
}

export function round(value, decimals) {
  return roundNoFixed(value, decimals).toFixed(decimals);
}

export function roundNoFixed(value, decimals) {
  const decimalFix = `${1}e${decimals}`;
  return Number(Math.round((value + Number.EPSILON) * decimalFix) / decimalFix);
}

export function useQuery(useMemo, useLocation) {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export function searchInCompartmentArray(value, indexArray, valuesDict) {
  const exactMatch = valuesDict[value];
  if (exactMatch !== undefined) {
    return exactMatch;
  }
  const closestIndexInf = indexArray.reduce((prev, curr) => (curr < value && curr > prev ? curr : prev));
  const closestIndexSup = indexArray[indexArray.indexOf(closestIndexInf) + 1];

  const closestValueSup = valuesDict[closestIndexInf];
  const closestValueInf = valuesDict[closestIndexSup];
  const infToSupDistanceProportion = (value - closestIndexInf) / (closestIndexSup - closestIndexInf);
  const newValue = closestValueSup
    - infToSupDistanceProportion * (closestValueSup - closestValueInf);
  return newValue;
}

export function downloadCsvFile(name, csv) {
  const hiddenElement = document.createElement('a');
  hiddenElement.href = `data:text/csv;charset=utf-8,${encodeURI(csv)}`;
  hiddenElement.target = '_blank';
  hiddenElement.download = `${name}_${new Date().toLocaleDateString()}.csv`;
  hiddenElement.click();
}

export function nominalVolumeFromCompartment(comp, t) {
  if (comp.nominalVolume === undefined || comp.nominalVolume === null) {
    return t('operation.not_known');
  }
  return roundNoFixed(
    unit(comp.nominalVolume, 'L').toNumber('hL'),
    unitMasks.hL,
  );
}

// export function localeFinder(locale) {
//   const localeMap = {
//     en: enLocale,
//     fr: frLocale,
//   };
//   return localeMap[locale];
// }

export function replaceComma(value) {
  if (value) {
    return value.toString().replace(/,/, '.');
  }
  return value;
}

export function navigatorNetworkState() {
  // console.log('Navigator online: ', navigator.onLine);
  return navigator.onLine ? 'online' : 'offline';
}

export const getValidAccessGrants = async (db, user, activeOrganization) => {
  const basePath = activeOrganization ? [
    'organizations', activeOrganization, 'accessGrants',
  ] : [
    'users', user?.uid, 'accessGrants',
  ];

  const accessGrantsRef = collection(db, ...basePath);

  const now = new Date();
  const validGrantsQuery = query(
    accessGrantsRef,
    where('expirationDate', '>', now),
    where('status', '==', 'active'),
  );

  const querySnapshot = await getDocs(validGrantsQuery);
  const validGrants = [];
  const grantNames = new Set();

  querySnapshot.forEach((d) => {
    validGrants.push(d.data());
    grantNames.add(d.data().name);
  });

  const uniqueGrantNames = Array.from(grantNames);

  return {
    validGrants,
    uniqueGrantNames,
  };
};

export const getKeyByNestedProperty = (obj, nestedProp, value) => {
  for (const [key, nestedObj] of Object.entries(obj)) {
    if (nestedObj[nestedProp] === value) {
      return key;
    }
  }
  return null; // Return null if no match is found
};

export const userInventoryAppAccess = (accessGrantNames) => {
  const requiredGrants = ['law-vsop', 'law-xo'];
  const hasRequiredGrants = requiredGrants.some((grant) => accessGrantNames.includes(grant));

  if (!hasRequiredGrants) {
    return 'none';
  }

  if (accessGrantNames.includes('law-xo')) {
    return 'full';
  }

  if (accessGrantNames.includes('law-vsop')) {
    return 'calculs';
  }

  return 'none';
};

export const dateFromTimeStampOrDate = (obj) => {
  if (obj?.seconds !== undefined && obj?.nanoseconds !== undefined) {
    // Convert Firestore timestamp to JavaScript Date object
    return new Date(obj.seconds * 1000 + obj.nanoseconds / 1000000);
  } if (obj?.toDate) {
    return obj.toDate();
  }
  return obj;
};

// eslint-disable-next-line no-promise-executor-return
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

export const refreshSWAndReload = async () => {
  // This will directly look for the service worker if it is
  // waiting and skip it then reload the page
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready.then((readyRegistration) => {
      if (readyRegistration.waiting) {
        readyRegistration.waiting.postMessage({ type: 'SKIP_WAITING' });
      }
    });
  }
  await sleep(2000);
  window.location.reload();
};

export const clearApplicationCache = async () => {
  localStorage.clear();
  sessionStorage.clear();

  // Clearing cookies
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i += 1) {
    const cookie = cookies[i];
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos) : cookie;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }

  if ('caches' in window) {
    const keys = await caches.keys();
    await Promise.all(keys.map((key) => caches.delete(key)));
  }

  window.location.reload();
};

export const isFirestoreEqual = (value1, value2) => {
  // Handle arrays
  if (Array.isArray(value1) && Array.isArray(value2)) {
    if (value1.length !== value2.length) return false;
    return value1.every((item, index) => isFirestoreEqual(item, value2[index]));
  }

  // Handle Firestore Timestamps
  if (value1?.toMillis && value2?.toMillis) {
    return value1.toMillis() === value2.toMillis();
  }

  // Handle plain objects
  if (typeof value1 === 'object' && value1 !== null
      && typeof value2 === 'object' && value2 !== null) {
    const keys1 = Object.keys(value1);
    const keys2 = Object.keys(value2);
    if (keys1.length !== keys2.length) return false;
    return keys1.every((key) => isFirestoreEqual(value1[key], value2[key]));
  }

  // Handle primitives
  return value1 === value2;
};

// UNUSED METHOD COMMENTED BELOW
// MIGHT STILL BE USEFUL SOMEDAY DEPENDING ON APP PERFORMANCE REGARDING OFFLINE MODE

// async function fetchWithTimeout(resource, options = {}) {
//   const { timeout = 8000 } = options;

//   const controller = new AbortController();
//   const id = setTimeout(() => controller.abort(), timeout);
//   const response = await fetch(resource, {
//     ...options,
//     signal: controller.signal,
//   });
//   clearTimeout(id);
//   return response;
// }

// export async function fetchNetworkState(functions) {
//   try {
//     const response = await fetchWithTimeout('http://neverssl.com', {
//       timeout: 6000,
//     });
//     return response.status >= 200 && response.status < 300 ? 'online' : 'offline';
//   } catch (err) {
//     return 'offline';
//   }
// }

// export function navigatorNetworkState2() {
//   // This isn't reliable for a lot of devices
// see: https://developer.mozilla.org/en-US/docs/Web/API/NetworkInformation
//   // That is why we will use fetchNetworkState to make sure at app start
//   const connection = navigator.connection
//     || navigator.mozConnection
//     || navigator.webkitConnection
//     || navigator.webkitConnection;

//   if (connection?.downlink <= 0.1) {
//     return 'offline';
//   }

//   // This is less precise and for iOS support
//   if (navigator.onLine === false) {
//     return 'offline';
//   }

//   return 'online';
// }
